import React from 'react';

import {Box} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import {useHistory} from 'react-router-dom';

import {
    EfeAccordionValues,
    EfeResponse,
    Field,
    LegalRepresentativeServiceRequest,
    View,
} from 'commons/models';
import {
    ATTR_NULL,
    Constants,
    HistoryRoutes,
    Messages,
    PersonTypes,
    SnackbarVariants,
    ViewKeys,
} from 'commons/constants';
import {EfeAccordion} from 'components';
import {MainContent} from 'layouts/commons';
import {useFlowFieldSearch, useSpinner} from 'hooks';
import RepresentativePersonalInfoForm from './representative-personal-info-form/RepresentativePersonalInfoForm';
import {formatDateToISO, nextStep} from 'commons/utils/Functions';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {ClientService} from 'services';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';
import {fieldCatalogGetter} from 'hooks/FlowHook';
import {Utils} from 'commons';
import {RepresentativeInfo} from 'commons/types/Types';

function RepresentativePersonalInfo() {
    const history = useHistory();
    const {flow, updateModuleStep} = useFlowContext();
    const {
        setTempRepresentative,
        selectedRepresentative,
        setSelectedRepresentative,
        representatives,
        setRepresentatives,
    } = useRelatedPeopleContext();
    const [viewFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const [fields, setFields] = React.useState<Array<Field>>([]);
    const {personType, menu, expedientId, fatherKycId, kycId, setKycId} = useMenuContext();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [auxLegalRepresentative, setAuxLegalRepresentative] = React.useState<any>(undefined);
    const {enqueueSnackbar} = useSnackbar();

    function getRepresentativeInfo(
        data: LegalRepresentativeServiceRequest,
        traded: boolean,
        kycId?: number
    ): RepresentativeInfo {
        return {
            kycId: kycId ? kycId : data.kycId,
            completeName: `${data.name} ${data.lastName} ${data.secondLastName}`,
            traded: traded,
            foreignCitizen: data.foreignCitizen,
        };
    }

    React.useEffect(() => {
        if (!fields || (fields && fields.length <= 0)) {
            setFields(viewFields);
        }
        if (
            fatherKycId > 0 &&
            kycId > 0 &&
            selectedRepresentative > 0 &&
            ViewKeys.LEGAL_PERSONAL_INFO === flow.data.expandKey
        ) {
            (async function () {
                try {
                    setIsLoading(true);
                    const response: EfeResponse<LegalRepresentativeServiceRequest> =
                        await ClientService.getLegalRepresentativeData(kycId, expedientId);
                    if (response && response.data) {
                        setAuxLegalRepresentative(response.data);
                        setTempRepresentative({...getRepresentativeInfo(response.data, false)});
                    }
                    setIsLoading(false);
                } catch (error) {
                    setIsLoading(false);
                    setAuxLegalRepresentative(undefined);
                }
            })();
        }
    }, []);

    function overrideFields(view: View) {
        if (view && view.fields) {
            setFields(view.fields);
        } else {
            setFields(viewFields);
        }
    }

    async function updateRepresentativeList(data: RepresentativeInfo) {
        setTempRepresentative(data);
        const aux: Array<RepresentativeInfo> = [...representatives];
        const index = aux.findIndex((item) => item.kycId === data.kycId);
        if (index >= 0) {
            aux[index] = {...data};
            setRepresentatives([...aux]);
        } else {
            setRepresentatives([...aux, data]);
        }
    }

    async function onSubmit(values: LegalRepresentativeServiceRequest) {
        const request: LegalRepresentativeServiceRequest = Utils.functions.clearUnusedFields(
            {...values},
            fields
        );
        request.viewId = flow.data.viewId;
        request.expedientId = expedientId;
        request.personTypeKey = PersonTypes.FISICA;
        request.rol = Constants.LEGAL_REPRESENTATIVE;
        request.birthDate = formatDateToISO(values.birthDate);
        request.viewFatherId = flow.data.fatherId;
        request.fatherKycId = fatherKycId;
        if (kycId >= 0) {
            request.kycId = kycId;
        }
        try {
            setIsLoading(true);
            const response: EfeResponse<LegalRepresentativeServiceRequest> =
                await ClientService.saveLegalRepresentative(request);
            if (response && response.data) {
                setKycId(response.data.kycId);
                updateRepresentativeList({
                    ...getRepresentativeInfo(request, false, response.data.kycId),
                });
                enqueueSnackbar(Messages.INFORMATION_SAVED_SUCCESSFULLY, {
                    variant: SnackbarVariants.SUCCESS,
                });
                const path = nextStep(flow, menu);
                setSelectedRepresentative(response.data.kycId);
                if (Constants.MODULE_STEP !== path) {
                    setIsLoading(false);
                    history.push(path);
                } else {
                    updateModuleStep(true);
                    flow.data.expandKey = path;
                    setIsLoading(false);
                }
            } else {
                setFields([...fields]);
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            setFields([...fields]);
            enqueueSnackbar(Messages.ERROR_SAVING_INFORMATION, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    const accordion: Array<EfeAccordionValues> = [
        {
            icon: <ActionIcon />,
            title: 'Informaci\u00F3n personal del Representante Legal o Apoderado Legal',
            reactElement: (
                <Box mt={8}>
                    {!isLoading && (
                        <RepresentativePersonalInfoForm
                            fields={[
                                ...fieldCatalogGetter(
                                    fields.filter((field) => ATTR_NULL !== field.attrName)
                                ),
                            ].sort((a: Field, b: Field) => (a.idOrder < b.idOrder ? -1 : 1))}
                            personType={personType}
                            submitHandler={onSubmit}
                            fieldHandler={overrideFields}
                            viewId={flow.data.viewId}
                            viewKey={flow.data.id}
                            auxLegalRepresentative={auxLegalRepresentative}
                        />
                    )}
                </Box>
            ),
            expandIcon: true,
            disabled: false,
            expanded: ViewKeys.LEGAL_PERSONAL_INFO === flow.data.expandKey,
        },
    ];

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}

            <EfeAccordion
                onChange={() => history.push(HistoryRoutes.REPRESENTATIVE_PERSONAL)}
                values={accordion}
            />
        </MainContent>
    );
}

export default RepresentativePersonalInfo;
